import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { GmailInvitePreview } from '../services/gmailInviteService';
import { maskEmail, maskName, recordingBlur } from '../utils/recordingBlur';

/** Gmail's reading pane shows an unstyled email in small Arial with blue links; the frame does the same. */
const FRAME_CSS = 'html{overflow:hidden}html,body{margin:0;background:#fff}'
  + 'body{display:flow-root;font:small/1.5 Arial,Helvetica,sans-serif;color:#222;overflow-wrap:anywhere}'
  + 'a{color:#1155cc}';

const frameDocument = (html: string): string => (
  `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>${FRAME_CSS}</style></head>`
  + `<body>${html}</body></html>`
);

/** The email's own HTML in a sandboxed frame (no scripts, its links open nothing), as tall as the email. */
const EmailBody: React.FC<{ html: string }> = ({ html }) => {
  const frame = useRef<globalThis.HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const fit = useCallback(() => {
    const body = frame.current?.contentDocument?.body;

    if (body) setHeight(body.scrollHeight);
  }, []);

  useEffect(() => {
    const node = frame.current;

    if (!node || typeof globalThis.ResizeObserver !== 'function') return undefined;
    const observer = new globalThis.ResizeObserver(fit);

    observer.observe(node);

    return () => observer.disconnect();
  }, [fit]);

  return (
    <iframe className="invite-email__frame" onLoad={fit} ref={frame} sandbox="allow-same-origin"
      srcDoc={frameDocument(html)} style={{ height }} title="The email's text" />
  );
};

const masked = (person: string): string => (person.includes('@') ? maskEmail(person) : maskName(person));

/** Who it goes to: the caller's description, or the person the preview was made for (masked with ?blur=1). */
const recipientLine = (preview: GmailInvitePreview, to?: string): string => {
  const person = preview.to_name || preview.to;

  if (to) return to;
  if (!person) return 'each person you pick, by name';

  return recordingBlur() ? masked(person) : person;
};

/** The invitation as the person will see it in Gmail: a white message view around the email's real HTML. */
export const InviteEmailCard: React.FC<{ preview: GmailInvitePreview | null; to?: string }> = ({ preview, to }) => {
  if (!preview) {
    return <div aria-busy="true" className="invite-email invite-email--loading">Loading the email…</div>;
  }
  const sender = preview.sender_name || preview.sender_email || preview.from;

  return (
    <article aria-label="The invitation email" className="invite-email">
      <h4 className="invite-email__subject">{preview.subject}</h4>
      <div className="invite-email__sender">
        <span aria-hidden="true" className="invite-email__avatar">{sender.trim().charAt(0).toUpperCase()}</span>
        <div>
          <p className="invite-email__from">
            <strong>{sender}</strong>
            {preview.sender_name && preview.sender_email && <span> &lt;{preview.sender_email}&gt;</span>}
          </p>
          <p className="invite-email__to">to {recipientLine(preview, to)}</p>
        </div>
      </div>
      <EmailBody html={preview.html} />
    </article>
  );
};
